/**
 * Currency Formatter Utility
 *
 * Formats price amounts according to German locale conventions:
 * - Thousands separator: . (dot)
 * - Decimal separator: , (comma)
 * - Currency symbol: € (euro)
 * - Locale: de-DE (fixed)
 *
 * Default behavior: Always show decimals
 * - Whole numbers: "1.500,‑ €" (with non-breaking hyphen)
 * - With decimals: "1.500,50 €"
 */

/** Non-breaking space character */
const NBSP = '\u00A0';
/** Non-breaking hyphen character */
const NB_HYPHEN = '\u2011';

/**
 * Format a price amount for display
 *
 * @param amount - Numeric price amount
 * @param hideDecimalsIfWhole - When true, hide decimals for whole numbers (default: false)
 * @param plainText - When true, use regular space instead of nbsp (for aria-labels, etc.)
 * @returns Formatted price string with currency symbol
 *
 * @example
 * formatPrice(1500) // "1.500,‑ €" (with non-breaking characters)
 * formatPrice(1500.50) // "1.500,50 €"
 * formatPrice(1500, true) // "1.500 €"
 * formatPrice(1500, false, true) // "1.500,- €" (plain text for aria)
 */
export function formatPrice(
  amount: number,
  hideDecimalsIfWhole = false,
  plainText = false
): string {
  const locale = 'de-DE';

  // Check if amount has decimals
  const hasDecimals = amount % 1 !== 0;

  // Format with Intl.NumberFormat for proper locale formatting
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: hasDecimals || !hideDecimalsIfWhole ? 2 : 0,
    maximumFractionDigits: 2,
  });

  const formattedNumber = formatter.format(amount);

  // Use non-breaking or regular characters based on plainText flag
  const space = plainText ? ' ' : NBSP;
  const hyphen = plainText ? '-' : NB_HYPHEN;

  // For whole numbers with default behavior, replace ",00" with ",-"
  if (!hasDecimals && !hideDecimalsIfWhole) {
    return `${formattedNumber.replace(',00', `,${hyphen}`)}${space}€`;
  }

  return `${formattedNumber}${space}€`;
}
